import { InjectionToken } from '@angular/core';
import { Program } from '@app/app/page-modules/programs/models/program.model';
import { CardsLayoutType, EditorContent, PlaylistLayoutType } from '@app/app/shared/models';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';

export interface ProgramMainSectionRequest {
  uid?: string;
  title: string;
  content?: EditorContent[];
  cardsLayoutType?: CardsLayoutType;
  layoutType: PlaylistLayoutType;
  imageName?: string;
  imageContentLength?: number;
  cards?: string[];
}

export interface ProgramCreationRequest {
  mainSection: ProgramMainSectionRequest;
  pageId?: string;
  pageSectionId?: string;
  targetGroups?: string[];
  languageCode?: string;
}

export interface CreateProgramResponse {
  item: Program;
  uploadUrl: string | null;
}

export interface ProgramUpdateRequest {
  mainSection: ProgramMainSectionRequest;
  standardSections?: ProgramSectionRequestUnion[];
}

export interface UpdateProgramResponse {
  uri: string;
  formattedUri: string;
  coverImageUploadUrl: string | null;
}

export type ProgramSectionType =
  | 'TEXT_AREA'
  | 'FEATURED'
  | 'CONTINUE_PLAYLISTS'
  | 'LATEST_PLAYLISTS'
  | 'FRAMEWORK_TRACKER'
  | 'CALLOUT_FEATURED'
  | 'UPCOMING'
  | 'YOUR_COLLECTIONS';

export interface ProgramSectionRequest {
  type: ProgramSectionType;
  title?: string;
  afterSection?: string;
}

export interface ProgramTextAreaSectionRequest extends ProgramSectionRequest {
  type: 'TEXT_AREA';
  content?: EditorContent[];
}

export interface ProgramFeaturedSectionRequest extends ProgramSectionRequest {
  type: 'FEATURED';
  items?: { playlistUid: string; cardUid?: string }[];
  content?: EditorContent[];
  colour?: string;
}

export interface ProgramCalloutSectionRequest extends ProgramSectionRequest {
  type: 'CALLOUT_FEATURED';
  items?: { playlistUid: string; cardUid?: string }[];
  content?: EditorContent[];
  colour?: string;
}

export interface ProgramFrameworkTrackerSectionRequest extends ProgramSectionRequest {
  type: 'FRAMEWORK_TRACKER';
  tags: string[];
}

export interface ProgramAutomaticSectionRequest extends ProgramSectionRequest {
  type: 'CONTINUE_PLAYLISTS' | 'LATEST_PLAYLISTS' | 'UPCOMING' | 'YOUR_COLLECTIONS';
}

export type ProgramSectionRequestUnion =
  | ProgramTextAreaSectionRequest
  | ProgramFeaturedSectionRequest
  | ProgramCalloutSectionRequest
  | ProgramFrameworkTrackerSectionRequest
  | ProgramAutomaticSectionRequest;

export interface ProgramSectionResponse {
  uid?: string;
}

export interface CreateProgramFromTemplateRequest {
  templateUid: string;
  in?: string;
  section?: string;
  languageCode?: string;
}

export interface ProgramDataService {
  getPrograms(): ObservableResult<Page<Program>>;
  getProgram(programUri: string, languageCode?: string): ObservableResult<Program>;
  createProgram(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse>;
  createProgramFromTemplate(request: CreateProgramFromTemplateRequest): ObservableResult<CreateProgramResponse>;
  updateProgram(programUid: string, request: ProgramUpdateRequest, languageCode?: string): ObservableResult<UpdateProgramResponse>;

  addProgramSection(
    programUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse>;

  updateProgramSection(
    programUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<void>;

  deleteProgramSection(programUid: string, sectionUid: string): ObservableResult<void>;

  reorderProgramSections(programUid: string, request: { sections: string[] }): ObservableResult<void>;

  publishProgram(programUid: string): ObservableResult<void>;
  unpublishProgram(programUid: string): ObservableResult<void>;

  archiveProgram(programUid: string): ObservableResult<void>;
}

export const CORE_PROGRAM_DATA_SERVICE = new InjectionToken<ProgramDataService>('CORE_PROGRAM_DATA_SERVICE');
